import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CompanyProvider, useCompany } from "./context/CompanyContext";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Ideas from "./pages/Ideas";
import IdeaDetail from "./pages/IdeaDetail";
import CreateIdea from "./pages/CreateIdea";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Reporting from "./pages/Reporting";
import ExecutiveSummary from "./pages/ExecutiveSummary";
import ProcessMaps from "./pages/ProcessMaps";
import ProcessMapEditor from "./pages/ProcessMapEditor";
import ProcessMapDetail from "./pages/ProcessMapDetail";
import ProcessCaptures from "./pages/ProcessCaptures";
import NewProcessCapture from "./pages/NewProcessCapture";
import ProcessCaptureDetail from "./pages/ProcessCaptureDetail";
import Settings from "./pages/Settings";
import SOPList from "./pages/SOPList";
import SOPDetail from "./pages/SOPDetail";
import Processes from "./pages/Processes";
import ProcessDetailPage from "./pages/ProcessDetail";
import AuditLogs from "./pages/AuditLogs";
import SelectCompany from "./pages/SelectCompany";
import MasterAdmin from "./pages/MasterAdmin";
import Layout from "./components/Layout";
import "./App.css";

const ProtectedRoute = ({ children, requireCompany = true }) => {
  const { user, loading } = useAuth();
  const { activeCompanyId } = useCompany();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#235894]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Require an active company for normal app routes. Prefer the user
  // payload's active_company_id (set by the backend on login / select-company)
  // to avoid a one-render race against CompanyContext's useEffect.
  const effectiveCompany = activeCompanyId || user.active_company_id;
  if (requireCompany && !effectiveCompany) {
    return <Navigate to="/select-company" replace />;
  }

  return children;
};

const MasterAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_master_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

// Role-gated route — redirects submitters away from restricted pages
const RoleRoute = ({ children, minRole = "Approver" }) => {
  const { user } = useAuth();
  const RANK = { Submitter: 0, Approver: 1, "CI Manager": 2, master_admin: 3 };
  if ((RANK[user?.role] ?? 0) < (RANK[minRole] ?? 1) && !user?.is_master_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CompanyProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/select-company"
              element={
                <ProtectedRoute requireCompany={false}>
                  <SelectCompany />
                </ProtectedRoute>
              }
            />
            <Route
              path="/master-admin"
              element={
                <MasterAdminRoute>
                  <MasterAdmin />
                </MasterAdminRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="ideas" element={<Ideas />} />
              <Route path="ideas/new" element={<CreateIdea />} />
              <Route path="ideas/:id" element={<IdeaDetail />} />
              <Route path="ideas/:id/edit" element={<CreateIdea />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="reporting" element={<RoleRoute><Reporting /></RoleRoute>} />
              <Route path="executive-summary" element={<RoleRoute><ExecutiveSummary /></RoleRoute>} />
              <Route path="process-maps" element={<ProcessMaps />} />
              <Route path="process-maps/new" element={<ProcessMapEditor />} />
              <Route path="process-maps/:id" element={<ProcessMapDetail />} />
              <Route path="process-maps/:id/edit" element={<ProcessMapEditor />} />
              <Route path="settings" element={<RoleRoute minRole="CI Manager"><Settings /></RoleRoute>} />
              <Route path="audit-log" element={<RoleRoute minRole="CI Manager"><AuditLogs /></RoleRoute>} />
              <Route path="processes" element={<Processes />} />
              <Route path="processes/:id" element={<ProcessDetailPage />} />
              <Route path="process-capture" element={<ProcessCaptures />} />
              <Route path="process-capture/new" element={<NewProcessCapture />} />
              <Route path="process-capture/:id" element={<ProcessCaptureDetail />} />
              <Route path="sop-generator" element={<SOPList />} />
              <Route path="sop-generator/:id" element={<SOPDetail />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CompanyProvider>
    </AuthProvider>
  );
}

export default App;
